import { useRef, useState } from 'react'

import { Box, Flex, FloraButton, FloraTypography, Toaster } from '@grupoboticario/flora-react'
import { EcommPaperClipIcon } from '@grupoboticario/flora-react-icons'

import { MAX_FILE_SIZE_BYTES } from '@/constants/accelerationForm'
import { processImageUpload } from '@/utils/imageUpload'

type SignedUrlResponse = {
  data?: {
    getEventSignedUrl?: {
      url?: string
      image?: string
    }
  }
}

interface AccelerationImageUploadProps {
  imageUrl?: string
  disabled?: boolean
  getSignedUrl: () => Promise<SignedUrlResponse>
  onBase64Change: (base64: string) => void
  onImageChange: (imageUrl: string, imageKey: string) => void
}

const AccelerationImageUpload = ({
  imageUrl: _imageUrl,
  disabled = false,
  getSignedUrl,
  onBase64Change,
  onImageChange
}: AccelerationImageUploadProps) => {
  const inputRef = useRef<HTMLInputElement>(null)
  const [isUploading, setIsUploading] = useState(false)

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    if (disabled) return
    const file = e.target.files?.[0] ?? null
    if (!file) return

    setIsUploading(true)

    await processImageUpload(
      file,
      getSignedUrl,
      (base64) => {
        onBase64Change(base64)
      },
      (_file, imageKey, previewUrl) => {
        onImageChange(previewUrl ?? '', imageKey)
        Toaster.notify({ kind: 'success', description: 'Imagem carregada com sucesso!' })
        setIsUploading(false)
      },
      (message) => {
        Toaster.notify({ kind: 'error', description: message })
        setIsUploading(false)
      },
      {
        allowedMimeTypes: ['image/png', 'image/jpeg', 'image/webp'],
        maxFileSizeBytes: MAX_FILE_SIZE_BYTES
      }
    )

    e.target.value = ''
  }

  return (
    <Box css={{ backgroundColor: '$light-dark-1', padding: '$2', borderRadius: '$2' }}>
      <Flex align="center" gap="$4">
        <FloraButton
          disabled={disabled || isUploading}
          has="iconLeft"
          hierarchy="primary"
          icon={<EcommPaperClipIcon />}
          onClick={() => inputRef.current?.click()}
          size="small"
          styleSemantic="neutral"
          type="button"
        >
          {isUploading ? 'Enviando...' : 'Inserir imagem'}
        </FloraButton>

        <FloraTypography css={{ color: '$medium-light-1' }} fontSize="exceptionsAuxiliar">
          Resolução recomendada: 1000 x 1000 px
        </FloraTypography>

        <input accept="image/png,image/jpeg,image/webp" hidden onChange={handleFileChange} ref={inputRef} type="file" />
      </Flex>
    </Box>
  )
}

export default AccelerationImageUpload
